import type { GeneratorState } from '../../model/use-agent-panel-store';
import type { BuildStep } from './build-flow-stepper';
import { pickCss } from '@/shared/lib/css-utils';
import { useAgentPanelStore } from '../../model/use-agent-panel-store';
import { BudgetStep } from './budget-step';
import { BuildFlowStepper } from './build-flow-stepper';
import { CommittingStep } from './committing-step';
import { DoneStep } from './done-step';
import { formatBriefBar, GeneratorHeader } from './generator-header';
import css from './generator-panel.module.scss';
import { OptionsStep } from './options-step';
import { PartialFailureStep } from './partial-failure-step';
import { PreviewStep } from './preview-step';

const styles = pickCss(css, css.root);

// Map the generator FSM phase onto the 3-step v3 build flow. Picking phases
// (options/preview/budget) all sit under "Day picks"; the commit-and-after
// phases sit under "Send". The thread-driven flow's "Conversation" step is
// owned by the inbox conversation page, not the generator drawer.
const buildStepForPhase = (
  phase: GeneratorState['phase'] | undefined
): BuildStep => {
  switch (phase) {
    case 'options':
    case 'preview':
    case 'budget':
      return 'day-picks';
    case 'committing':
    case 'done':
    case 'partial-failure':
      return 'send';
    default:
      return 'day-picks';
  }
};

export const GeneratorPanel = () => {
  const generatorState = useAgentPanelStore((s) => s.generatorState);
  const closePanel = useAgentPanelStore((s) => s.close);
  const phase = generatorState?.phase;

  // Only phases with 'options' and 'preview'/'budget' have extracted data
  const hasExtracted =
    generatorState !== null &&
    generatorState !== undefined &&
    'extracted' in generatorState;
  const briefBarText = hasExtracted
    ? formatBriefBar(generatorState.extracted)
    : '';

  return (
    <div className={styles()}>
      {/* v3 3-step Conversation › Day picks › Send header, shown above the
          per-phase chrome across the whole thread-driven build flow. */}
      <BuildFlowStepper
        active={buildStepForPhase(phase)}
        onStepClick={(step) => {
          // "Conversation" steps back out of the generator to the thread
          // conversation page (the drawer closes over it).
          if (step === 'conversation') closePanel();
        }}
      />
      {phase !== 'options' && (
        <GeneratorHeader briefBarText={briefBarText} showWorkflow={false} />
      )}
      {phase === 'options' && <OptionsStep />}
      {phase === 'preview' && <PreviewStep />}
      {phase === 'budget' && <BudgetStep />}
      {phase === 'committing' && <CommittingStep />}
      {phase === 'done' && <DoneStep />}
      {phase === 'partial-failure' && <PartialFailureStep />}
    </div>
  );
};
